import { Box, Flex, Stack } from "@chakra-ui/react";
import { useMemo } from "react";
import { Navigate, Route, Routes } from "react-router";

import "./App.css";
import { AccessRestrictedPage } from "./components/access-restricted-page";
import { AdminNav } from "./components/admin/AdminNav";
import { AppHeader } from "./components/app-header/app-header";
import { DevPanel } from "./components/dev-panel";
import { ErrorBanner } from "./components/error-banner";
import { Footer } from "./components/footer";
import { RoleProtectedRoute } from "./components/role-protected-route";
import { ScrollToTop } from "./components/scroll-to-top";
import { Toaster } from "./components/ui/toaster";
import { APP_ROUTES } from "./constants/routes";
import {
  AppRoute,
  ERROR_BANNER_HEIGHT_PX,
  MAX_PAGE_WIDTH,
  outage,
  portfolioTrackingOutage,
} from "./constants/shared";
import { useAnalytics } from "./hooks/useAnalytics";
import { useCompliance } from "./hooks/useCompliance";
import { initDeviceId } from "./hooks/useDeviceId";
import { useNetworkGuard } from "./hooks/useNetworkGuard";
import { AirdropSummary } from "./pages/Admin/Airdrop/AirdropSummary";
import { BattleGroupReview } from "./pages/Admin/BattleGroups/BattleGroupReview";
import { PointsCompare } from "./pages/Admin/Points/PointsCompare";
import { PortalStats } from "./pages/Admin/PortalStats";
import { UserAdmin } from "./pages/Admin/UserAdmin";
import { NotFound } from "./pages/error/NotFound";
import { Home } from "./pages/Home/Home";
import { Onboarding } from "./pages/Onboarding/Onboarding";
import { Providers } from "./providers/providers";

initDeviceId();

function App() {
  return (
    <Providers>
      <ScrollToTop />
      <ErrorBanner />
      <AppContent />
      <DevPanel />
      <Toaster />
    </Providers>
  );
}

const AppContent = () => {
  useAnalytics();
  useNetworkGuard();
  const { geoCompliance, isPendingGeoCompliance } = useCompliance();
  const restrictAccess = geoCompliance?.restrictAccess === true;
  const appMarginTop = useMemo(() => {
    let marginTop = 0;
    if (outage || portfolioTrackingOutage) {
      marginTop += ERROR_BANNER_HEIGHT_PX;
    }
    return marginTop;
  }, []);

  if (isPendingGeoCompliance) {
    return null;
  }

  if (restrictAccess) {
    return (
      <Stack direction="column" minH="100vh" gap={0}>
        <AppHeader />
        <Flex
          py={8}
          px={[2, null, null, 6]}
          w="full"
          h="full"
          minH="100vh"
          overflow="hidden"
          mt={16}
          bgColor="bg.accent"
          direction="column"
        >
          <Box
            flex={1}
            w="full"
            m="auto"
            maxW={MAX_PAGE_WIDTH}
            minH="full"
            display="grid"
            gridTemplateRows="1fr auto"
          >
            <AccessRestrictedPage />
            <Footer />
          </Box>
        </Flex>
      </Stack>
    );
  }

  return (
    <Box mt={`${appMarginTop}px`}>
      <Routes>
        <Route path="onboarding/:currentStep" element={<Onboarding />} />
        <Route
          path="onboarding/"
          element={<Navigate to="/onboarding/welcome" replace />}
        />
        <Route path="/404" element={<NotFound />} />
        <Route path="admin/*" element={<AdminLayout />} />
        <Route
          path="/*"
          element={
            <Stack direction={["column", "row"]} minH={"100vh"} gap={0}>
              <AppHeader />
              <Routes>
                {APP_ROUTES.map(({ path, element: Element }) => {
                  if (!Element) return null; // if element is null, it's not an internal route
                  return (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <WrappedRoute path={path} routeElement={<Element />} />
                      }
                    />
                  );
                })}

                <Route
                  path="/*"
                  element={<WrappedRoute path="/*" routeElement={<Home />} />}
                />
              </Routes>
            </Stack>
          }
        />
      </Routes>
    </Box>
  );
};

// Admin layout component for all admin pages
const AdminLayout = () => {
  return (
    <RoleProtectedRoute requiredRole="ADMIN">
      <Box
        py={8}
        px={[2, 2, 6]}
        w="full"
        h="full"
        overflow="hidden"
        bgColor="bg.accent"
      >
        <Box m="auto" maxW={MAX_PAGE_WIDTH}>
          <AdminNav />
          <Routes>
            <Route path="points/compare" element={<PointsCompare />} />
            <Route path="battlegroups/review" element={<BattleGroupReview />} />
            <Route path="airdrop/summary" element={<AirdropSummary />} />
            <Route path="portal/stats" element={<PortalStats />} />
            <Route path="users" element={<UserAdmin />} />
            {/* Add more admin routes here */}
            <Route
              path="*"
              element={<Navigate to="/admin/points/compare" replace />}
            />
          </Routes>
        </Box>
      </Box>
    </RoleProtectedRoute>
  );
};

export default App;

const WrappedRoute = ({
  path,
  routeElement,
}: {
  path: string;
  routeElement: React.ReactNode;
}) => {
  const isFullWidth = path === AppRoute.Bridge || path === AppRoute.Swap;

  return (
    <Flex
      py={8}
      px={[2, null, null, 6]}
      w="full"
      h="full"
      minH="100vh"
      overflow="hidden"
      mt={16}
      bgColor="bg.accent"
      direction="column"
    >
      <Box
        flex={1}
        w="full"
        m="auto"
        maxW={isFullWidth ? "full" : MAX_PAGE_WIDTH}
        minH="full"
        display="grid"
        gridTemplateRows="1fr auto"
      >
        {routeElement}
        <Footer />
      </Box>
    </Flex>
  );
};
